import { useSelector } from 'react-redux';
import { paths } from '../../common/constants/pathVariables';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import {
  additionalServicesMsg,
  barringServicesMsg,
  basicDetailsMsg,
  billingAccountAndUserMsg,
  broadbandMsg,
  internetSubscriptionsMsg,
  mobileBroadbandMsg,
  mobileM2MMsg,
  simCardMsg,
  t,
  technicalDataMsg,
} from '../../common/i18n';
import { formatPhoneNumber } from '../../common/utils/phoneNumberUtils';
import {
  getAddonAssociationsAndDependencies,
  getAvailableAddOnCodes,
  isSubscriptionInSfdc,
} from '../../common/utils/subscriptionUtils';
import { SubscriptionType } from '../../generated/api/subscriptionType';
import { MobileSubscriptionLayout, getSubscriptionUrl } from './MobileSubscriptionLayout';
import type { CategorySubscriptionLayoutProps } from './MobileSubscriptionLayout';
import type { Subscription } from '../../generated/api/subscription';
import type { State } from '../../selfservice/common/store.js';
import type { LocalNaviItem } from '../LocalNavi';

const getTopTitle = (subscription: Subscription) => {
  if (subscription.subscriptionType === SubscriptionType.MOBILE_BROADBAND) {
    return t.EQKJ(mobileBroadbandMsg);
  }
  if (subscription.subscriptionType === SubscriptionType.MOBILE_M2M) {
    return t.A7CA(mobileM2MMsg);
  }
  return t.VI0P(broadbandMsg);
};

const getTitle = (subscription: Subscription) => {
  if (
    subscription.subscriptionType === SubscriptionType.MOBILE_BROADBAND ||
    subscription.subscriptionType === SubscriptionType.MOBILE_M2M
  ) {
    return subscription.subscriptionPurposeOfUse ?? '…';
  }
  return subscription.subscriptionUserFriendlyId ?? subscription.subscriptionPurposeOfUse ?? '';
};

const getSubtitle = (subscription: Subscription, companyName?: string) => {
  if (
    subscription.subscriptionType === SubscriptionType.MOBILE_BROADBAND ||
    subscription.subscriptionType === SubscriptionType.MOBILE_M2M
  ) {
    const phoneNumber = formatPhoneNumber(subscription.details?.mobile?.mobileNumber ?? '');
    return `${phoneNumber}${phoneNumber ? ' | ' : ''}${companyName}`;
  }
  return companyName;
};

const getHeroPicto = (subscription: Subscription) => {
  if (subscription.subscriptionType === SubscriptionType.MOBILE_BROADBAND) {
    return 'mobilebb';
  }
  if (subscription.subscriptionType === SubscriptionType.MOBILE_M2M) {
    return 'mobile-m2m';
  }
  return 'fixedbb';
};

const getNavigationCategories = (
  subscription: Subscription,
  mdmId: string,
  hideAdditionalContent: boolean,
  hideBarringServices: boolean,
  hideBillingAccountAndUser: boolean
) => {
  const navigationCategories = [
    {
      children: t.C9M1(basicDetailsMsg),
      to: getSubscriptionUrl(paths.PS_BROADBAND_SUBSCRIPTION, subscription, mdmId),
    },
  ] as LocalNaviItem[];

  if (!hideBillingAccountAndUser) {
    navigationCategories.push({
      children: t.CZ63(billingAccountAndUserMsg),
      to: getSubscriptionUrl(paths.PS_BROADBAND_SUBSCRIPTION_BA_CONTRACT_PARTY, subscription, mdmId),
    });
  }

  if (hideAdditionalContent) {
    return navigationCategories;
  }

  if ([SubscriptionType.MOBILE_BROADBAND, SubscriptionType.MOBILE_M2M].includes(subscription.subscriptionType)) {
    navigationCategories.push({
      children: t.PIZC(simCardMsg),
      to: getSubscriptionUrl(paths.PS_BROADBAND_SUBSCRIPTION_SIM_CARD, subscription, mdmId),
    });
  }

  if (isSubscriptionInSfdc(subscription) && subscription.subscriptionType === SubscriptionType.MOBILE_BROADBAND) {
    if (!hideBarringServices) {
      navigationCategories.push({
        children: t.ZUCA(barringServicesMsg),
        to: getSubscriptionUrl(paths.PS_BROADBAND_SUBSCRIPTION_BARRING_SERVICES, subscription, mdmId),
      });
    }

    navigationCategories.push({
      children: t.LXSR(additionalServicesMsg),
      to: getSubscriptionUrl(paths.PS_BROADBAND_SUBSCRIPTION_ADDONS, subscription, mdmId),
    });
  }

  if (
    ([
      SubscriptionType.BROADBAND,
      SubscriptionType.CORPORATE_NETWORK,
      SubscriptionType.HOUSING_COMPANY_BROADBAND,
    ].includes(subscription.subscriptionType) &&
      subscription.details?.broadband) ||
    (subscription.subscriptionType === SubscriptionType.MOBILE_BROADBAND &&
      (subscription.details?.mobile?.deviceModel || subscription.details?.mobile?.serialNumber))
  ) {
    navigationCategories.push({
      children: t.L2MX(technicalDataMsg),
      to: getSubscriptionUrl(paths.PS_BROADBAND_SUBSCRIPTION_TECHNICAL_DETAILS, subscription, mdmId),
    });
  }

  return navigationCategories;
};

export const BroadbandSubscriptionLayout = ({
  addOnRules,
  addOnRulesMobilePbx,
  children,
  companyInfo,
  subscription,
}: CategorySubscriptionLayoutProps) => {
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  if (!mdmId) {
    throw new Error('missing mdmId');
  }
  const { suspendSubscriptionInitiated, terminateSubscriptionInitiated } = useSelector((s: State) => ({
    suspendSubscriptionInitiated: s?.selfservice?.pendingSubscriptionActions?.suspendSubscriptionInitiated ?? false,
    terminateSubscriptionInitiated: s?.selfservice?.pendingSubscriptionActions?.terminateSubscriptionInitiated ?? false,
  }));
  const hideAdditionalContent =
    !subscription?.details || suspendSubscriptionInitiated || terminateSubscriptionInitiated;
  const hideBillingAccountAndUser = suspendSubscriptionInitiated || terminateSubscriptionInitiated;
  const { associations } = getAddonAssociationsAndDependencies(subscription, addOnRules, addOnRulesMobilePbx);
  const availableAddOnCodes = getAvailableAddOnCodes(associations, subscription.commercialProductId);
  const hideBarringServices = !availableAddOnCodes.length;

  return (
    <MobileSubscriptionLayout
      subscription={subscription}
      headingTop={getTopTitle(subscription)}
      heading={getTitle(subscription)}
      headingBottom={getSubtitle(subscription, companyInfo?.companyName)}
      heroPicto={getHeroPicto(subscription)}
      navigationItems={getNavigationCategories(
        subscription,
        mdmId,
        hideAdditionalContent,
        hideBarringServices,
        hideBillingAccountAndUser
      )}
      categoryBreadcrumbAndPath={{ name: t.SEYV(internetSubscriptionsMsg), path: paths.PS_BROADBAND_SUBSCRIPTIONS }}
    >
      {children}
    </MobileSubscriptionLayout>
  );
};
